import React, { useState } from "react";
import { Popover, TextField, Tooltip } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

export const isOverWip = (count, limit) => Boolean(limit) && count > limit;

/**
 * A Kanban column's count against its work-in-progress limit ("2 / 3"), and —
 * for people who can configure the board — a small editor for the limit.
 * `onSave(limit | null)` persists it; null clears the limit.
 */
const WipLimitControl = ({ title, count = 0, limit = null, canEdit = false, onSave = async () => {} }) => {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const over = isOverWip(count, limit);

  const open = (event) => {
    setDraft(limit ? String(limit) : "");
    setAnchor(event.currentTarget);
  };

  const valid = draft === "" || (/^\d+$/.test(draft) && Number(draft) >= 1 && Number(draft) <= 99);

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await onSave(draft === "" ? null : Number(draft));
      setAnchor(null);
    } finally {
      setSaving(false);
    }
  };

  const label = limit
    ? `${count} of ${limit} allowed in ${title}${over ? " — over the WIP limit" : ""}`
    : `${count} in ${title}, no WIP limit`;

  return (
    <span className="wip-control">
      <Tooltip title={label}>
        <span className={`each-tasks-cls wip-count ${over ? "is-over" : ""}`} aria-label={label}>
          {count}
          {limit ? <span className="wip-count__limit">/{limit}</span> : null}
        </span>
      </Tooltip>
      {canEdit ? (
        <Tooltip title="Set WIP limit">
          <button type="button" className="wip-edit" onClick={open} aria-label={`Set WIP limit for ${title}`}>
            <TuneRoundedIcon />
          </button>
        </Tooltip>
      ) : null}
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: "wip-pop" } }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <label className="wip-pop__label" htmlFor={`wip-${title}`}>WIP limit for {title}</label>
          <p className="wip-pop__hint">Most issues allowed here at once. Leave empty for no limit.</p>
          <TextField
            id={`wip-${title}`}
            size="small"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value.trim())}
            error={!valid}
            helperText={valid ? " " : "A whole number from 1 to 99"}
            inputProps={{ inputMode: "numeric", "aria-label": `WIP limit for ${title}` }}
            fullWidth
          />
          <div className="wip-pop__actions">
            <button type="button" className="wip-pop__btn" onClick={() => setAnchor(null)}>Cancel</button>
            <button type="submit" className="wip-pop__btn is-primary" disabled={!valid || saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </Popover>
    </span>
  );
};

export default WipLimitControl;
